/**
 * useMediaPlanFlow — Cross-screen state for the self-serve media plan flow.
 *
 * Wraps self-serve routes via <CampaignFlowProvider> in App.jsx.
 * Holds data flowing between screens: business → plan → customization → campaign.
 * Persists campaignId to sessionStorage so the Generate screen survives refresh.
 */
import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';

// ── Initial state ───────────────────────────────────────────────────────────

const SESSION_KEY = 'cornercast_campaign_flow';

function loadPersistedState() {
  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    // ignore parse errors
  }
  return null;
}

const defaultState = {
  /** Business object from search step */
  business: null,
  /** Currently selected plan ID */
  selectedPlanId: null,
  /** Full plan object */
  plan: null,
  /** Array of available plans for this business */
  plans: null,
  /** Creative customization from "Make It Yours" */
  customization: {
    creativeBrief: '',
    voice: null,
    pronunciation: '',
    characters: [],
    logoFiles: [],
    imageFiles: [],
  },
  /** Targeting address for website URL flow (geocoded server-side) */
  targetingAddress: '',
  /** Campaign ID from payment step */
  campaignId: null,
  /** Full campaign object */
  campaign: null,
  /** Originating lead ID (when navigated from leads page) */
  leadId: null,
  /** URL to return to after plan flow (e.g. '/org/acme/leads') */
  returnTo: null,
};

function getInitialState() {
  const persisted = loadPersistedState();
  if (persisted) {
    return { ...defaultState, ...persisted };
  }
  return { ...defaultState };
}

// ── Reducer ─────────────────────────────────────────────────────────────────

function flowReducer(state, action) {
  switch (action.type) {
    case 'SET_BUSINESS':
      return { ...state, business: action.payload };

    case 'SET_PLANS':
      return { ...state, plans: action.payload };

    case 'SELECT_PLAN':
      return {
        ...state,
        selectedPlanId: action.payload.planId,
        plan: action.payload.plan,
      };

    case 'SET_TARGETING_ADDRESS':
      return { ...state, targetingAddress: action.payload };

    case 'SET_CUSTOMIZATION':
      return {
        ...state,
        customization: { ...state.customization, ...action.payload },
      };

    case 'SET_CAMPAIGN_ID':
      return { ...state, campaignId: action.payload };

    case 'SET_CAMPAIGN':
      return { ...state, campaign: action.payload };

    case 'SET_LEAD_ID':
      return { ...state, leadId: action.payload };

    case 'SET_RETURN_TO':
      return { ...state, returnTo: action.payload };

    case 'RESET':
      return { ...defaultState };

    default:
      return state;
  }
}

// ── Context ─────────────────────────────────────────────────────────────────

const CampaignFlowContext = createContext(null);

export function CampaignFlowProvider({ children }) {
  const [state, dispatch] = useReducer(flowReducer, null, getInitialState);

  // Persist key fields to sessionStorage on every change
  useEffect(() => {
    try {
      const toPersist = {
        business: state.business,
        selectedPlanId: state.selectedPlanId,
        targetingAddress: state.targetingAddress,
        campaignId: state.campaignId,
        leadId: state.leadId,
        returnTo: state.returnTo,
        // Don't persist large objects like plans/campaign/customization with files
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(toPersist));
    } catch {
      // Storage full or unavailable — fail silently
    }
  }, [state.business, state.selectedPlanId, state.targetingAddress, state.campaignId, state.leadId, state.returnTo]);

  // ── Actions ─────────────────────────────────────────────────────────────

  const setBusiness = useCallback((business) => {
    dispatch({ type: 'SET_BUSINESS', payload: business });
  }, []);

  const setPlans = useCallback((plans) => {
    dispatch({ type: 'SET_PLANS', payload: plans });
  }, []);

  const selectPlan = useCallback((planId, plan) => {
    dispatch({ type: 'SELECT_PLAN', payload: { planId, plan } });
  }, []);

  const setTargetingAddress = useCallback((address) => {
    dispatch({ type: 'SET_TARGETING_ADDRESS', payload: address });
  }, []);

  const setCustomization = useCallback((data) => {
    dispatch({ type: 'SET_CUSTOMIZATION', payload: data });
  }, []);

  const setCampaignId = useCallback((id) => {
    dispatch({ type: 'SET_CAMPAIGN_ID', payload: id });
  }, []);

  const setCampaign = useCallback((campaign) => {
    dispatch({ type: 'SET_CAMPAIGN', payload: campaign });
  }, []);

  const setLeadId = useCallback((id) => {
    dispatch({ type: 'SET_LEAD_ID', payload: id });
  }, []);

  const setReturnTo = useCallback((path) => {
    dispatch({ type: 'SET_RETURN_TO', payload: path });
  }, []);

  const reset = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    dispatch({ type: 'RESET' });
  }, []);

  const value = {
    ...state,
    setBusiness,
    setPlans,
    selectPlan,
    setTargetingAddress,
    setCustomization,
    setCampaignId,
    setCampaign,
    setLeadId,
    setReturnTo,
    reset,
  };

  return (
    <CampaignFlowContext.Provider value={value}>
      {children}
    </CampaignFlowContext.Provider>
  );
}

/**
 * Hook to access the campaign flow context.
 * Must be used within <CampaignFlowProvider>.
 */
export default function useMediaPlanFlow() {
  const context = useContext(CampaignFlowContext);
  if (!context) {
    throw new Error('useMediaPlanFlow must be used within a CampaignFlowProvider');
  }
  return context;
}
